'use client';

import { CustomerDetail, useCustomerById } from '@/lib/api/customers';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import CustomerDetails from '@/app/customers/[id]/components/customer-details';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { CustomerDetailsSkeleton } from './components/customer-details-skeleton';
import { CustomerHeader } from './components/customer-header';
import { CustomerPayments } from './components/customer-payments';
import { CustomerSubscriptions } from './components/customer-subscriptions';
import CustomerDevices from './components/customer-devices';
import { CustomerEventTrace } from './components/customer-event-trace';
import { CustomerCredits } from './components/customer-credits';

interface CustomerDetailsPageProps {
  customer: CustomerDetail;
}

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(CustomerDetailsPageContainer, PERMISSIONS.CUSTOMERS_VIEW);

function CustomerDetailsPageContainer() {
  const { t } = useTranslation('customers');
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useCustomerById(id || '');

  if (isLoading) {
    return <CustomerDetailsSkeleton />;
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('details.not_found')}</div>;
  }

  return <CustomerDetailsPage customer={data} />;
}

const CustomerDetailsPage = ({ customer }: CustomerDetailsPageProps) => {
  const { t } = useTranslation('customers');

  return (
    <div className="container mx-auto">
      <CustomerHeader customer={customer} />
      <div className="my-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <CustomerPayments customer={customer} />
          <CustomerSubscriptions customer={customer} />
          <CustomerDevices customer={customer} />
          <CustomerEventTrace customerId={customer.id} />
        </div>
        <div className="space-y-6 max-lg:order-first">
          <CustomerDetails customer={customer} />
          <CustomerCredits customer={customer} />
        </div>
      </div>
    </div>
  );
};
